<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import { useForm } from "vee-validate"
import * as yup from "yup"
import { OptionTarifaire, PLAGE_ORDER } from "@modules/courtage/constants"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import { toTypedSchema } from "@vee-validate/yup"
import BaseInputNumber from "primevue/inputnumber"
import {
    ComparatorEnergieParPosteElec,
    CourtageFormFieldConfig,
    Supplier,
} from "@types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: ({ named }: { named: (key: string) => string }) =>
                named("actual")
                    ? "Fournisseur et info. générales de l'offre de référence"
                    : "Fournisseur et info. générales de l'offre alternative",
            supplier: "Nom du fournisseur *",
            offer_name: "Nom de l'offre *",
            offer_type: "Type de l'offre *",
            offer_type_options: {
                [OptionTarifaire.Base]: "Base",
                [OptionTarifaire.HPHC]: "Heures pleines et creuses",
                [OptionTarifaire.HS]: "Horosaisonnier",
                [OptionTarifaire.HS_PTE_Fixe]: "Horosaisonnier avec Pointe",
            },
            edit: ({ named }: { named: (key: string) => string }) =>
                named("edit") ? "Valider" : "Remplir",
            use_energieparposte_elec: "Éditer la consommation annuelle",
            energieparposte_elec_form: {
                title: "Consommation annuelle",
                suffix: "kWh",
                fields: {
                    HCB: {
                        title: "Heures creuses saison basse (HCB)",
                        tooltip:
                            "Consommation durant les heures creuses saison basse",
                    },
                    HCH: {
                        title: "Heures creuses saison haute (HCH)",
                        tooltip:
                            "Consommation durant les heures creuses saison haute",
                    },
                    HPB: {
                        title: "Heures pleines saison basse (HPB)",
                        tooltip:
                            "Consommation durant les heures pleines saison basse",
                    },
                    HPH: {
                        title: "Heures pleines saison haute (HPH)",
                        tooltip:
                            "Consommation durant les heures pleines saison haute",
                    },
                    PTE: {
                        title: "Pointe fixe",
                        tooltip: "Consommation en heure de pointe fixe",
                    },
                    HC: {
                        title: "Heures creuses (HC)",
                        tooltip: "Consommation durant les heures creuses",
                    },
                    HP: {
                        title: "Heures pleines (HP)",
                        tooltip: "Consommation durant les heures pleines",
                    },
                    Base: {
                        title: "Base",
                        tooltip: "Consommation en tarif base",
                    },
                },
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    actual: boolean
    name: string
    offer: OptionTarifaire | ""
    supplier: string
    useEnergieparposteElec: boolean
    energieparposteElec?: ComparatorEnergieParPosteElec
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    actual: false,
    name: "",
    offer: "",
    supplier: "",
    useEnergieparposteElec: false,
    energieparposteElec: undefined,
})

const emit = defineEmits<{
    (e: "update:supplier", value: string): void
    (e: "update:name", value: string): void
    (e: "update:offer", value: OptionTarifaire | ""): void
    (e: "update:use-energieparposte-elec", value: boolean): void
    (
        e: "update:energieparposte-elec",
        value: ComparatorEnergieParPosteElec
    ): void
}>()

// ================================
// STORE & DONNÉES DU COMPARATEUR
// ================================
/**
 * Store du comparateur pour gérer les fournisseurs et sources
 */
const comparatorStore = useComparatorStore()

/**
 * Données réactives extraites du store
 */
const {
    suppliers,
    isFetchingSuppliers,
    getSourceById,
    consommationByPlageFournisseur,
    useEnergieparposteElec: useEnergieparposteElecStore,
    snapshotConsommationByPlageFournisseur,
    isFetchingConsoParPlage,
} = storeToRefs(comparatorStore)

// ================================
// ÉTAT DE L'INTERFACE
// ================================
/**
 * État d'ouverture/fermeture de l'accordéon
 */
const active = ref<string | null>(null)

// ================================
// VALIDATION DU FORMULAIRE
// ================================
/**
 * Génère le schéma de validation selon si l'offre est actuelle ou non
 * @returns Schéma de validation Yup
 */
function getSchema(): yup.ObjectSchema<{
    supplier: string
    name: string
    offer: OptionTarifaire | ""
    useEnergieparposteElec?: boolean
    Base?: number
    HP?: number
    HC?: number
    HPH?: number
    HCH?: number
    HPB?: number
    HCB?: number
    PTE?: number
}> {
    const communSchema = {
        supplier: yup.string().required(),
        name: yup.string().required(),
        offer: yup.string().required().oneOf(Object.values(OptionTarifaire)),
    }

    // Si offre alternative alors pas de validation spécifique
    if (!props.actual) {
        return yup.object({
            ...communSchema,
            useEnergieparposteElec: yup.boolean().optional(),
            Base: yup.number().optional(),
            HP: yup.number().optional(),
            HC: yup.number().optional(),
            HPH: yup.number().optional(),
            HCH: yup.number().optional(),
            HPB: yup.number().optional(),
            HCB: yup.number().optional(),
            PTE: yup.number().optional(),
        })
    }

    // Configuration des champs energieparposteElec selon l'option tarifaire
    const energieparposteElecConfig: Record<
        OptionTarifaire,
        (keyof ComparatorEnergieParPosteElec)[]
    > = {
        [OptionTarifaire.Base]: ["Base"],
        [OptionTarifaire.HPHC]: ["HP", "HC"],
        [OptionTarifaire.HS]: ["HPH", "HCH", "HPB", "HCB"],
        [OptionTarifaire.HS_PTE_Fixe]: ["HPH", "HCH", "HPB", "HCB", "PTE"],
    }

    // Ajout des validations pour chaque champ dynamique en fonction de l'option tarifaire
    // Génération dynamique des schémas de coefficients
    const offer = props.offer as OptionTarifaire
    // Validation explicite de l'offre avant utilisation
    if (!offer) {
        return yup.object({
            ...communSchema,
        })
    }
    const energieparposteElecField = (
        energieparposteElecConfig[offer] || []
    ).reduce(
        (acc, field) => {
            acc[field] = yup
                .number()
                .typeError("Doit être un nombre")
                .min(0, "Doit être supérieur ou égal à 0")
                .required("Champ requis")
            return acc
        },
        {} as Record<keyof ComparatorEnergieParPosteElec, yup.Schema>
    )

    return yup.object({
        ...communSchema,
        useEnergieparposteElec: yup.boolean().required(),
        ...energieparposteElecField,
    })
}

/**
 * Schéma de validation dynamique selon l'option tarifaire
 * @returns {yup.ObjectSchema<any>} Schéma de validation Yup
 */
const typedSchema = computed(() => toTypedSchema(getSchema()))
/**
 * Configuration du formulaire avec validation Yup
 */
const { errors, defineField, meta, validate } = useForm({
    validationSchema: typedSchema,
    initialValues: {
        supplier: props.supplier,
        name: props.name,
        offer: props.offer,
        useEnergieparposteElec: props.useEnergieparposteElec,
        Base: props.energieparposteElec?.Base ?? undefined,
        HP: props.energieparposteElec?.HP ?? undefined,
        HC: props.energieparposteElec?.HC ?? undefined,
        HPH: props.energieparposteElec?.HPH ?? undefined,
        HCH: props.energieparposteElec?.HCH ?? undefined,
        HPB: props.energieparposteElec?.HPB ?? undefined,
        HCB: props.energieparposteElec?.HCB ?? undefined,
        PTE: props.energieparposteElec?.PTE ?? undefined,
    },
})

/**
 * Champs du formulaire avec validation
 */
const [modelSupplier] = defineField("supplier")
const [modelName] = defineField("name")
const [modelOffer] = defineField("offer")

// Champ pour l'usage des données énergie par poste électrique
const [modelUseEnergieparposteElec] = defineField("useEnergieparposteElec")
// Champ pour les coefficients énergie par poste électrique
const [modelEnergieBase] = defineField("Base")
const [modelEnergieHp] = defineField("HP")
const [modelEnergieHc] = defineField("HC")
const [modelEnergieHph] = defineField("HPH")
const [modelEnergieHch] = defineField("HCH")
const [modelEnergieHpb] = defineField("HPB")
const [modelEnergieHcb] = defineField("HCB")
const [modelEnergiePte] = defineField("PTE")

// ================================
// DONNÉES MÉTIER - PLAGES HORAIRES
// ================================
/**
 * Plages horaires extraites de la source sélectionnée pour déterminer les types d'offres disponibles
 */
const getPlagesHoraires = computed(() => {
    return (
        getSourceById.value?.informations?.pdl?.comptage?.plages_horaires ?? {
            heures_creuses: [],
            heures_pointe_fixe: [],
            pointes: [],
        }
    )
})

// ================================
// LOGIQUE MÉTIER - TYPES D'OFFRES
// ================================
/**
 * Filtre les types d'offres disponibles selon le segment du compteur et les plages horaires
 * @returns Liste des options tarifaires filtrées selon les critères métier
 */
const getOfferTypeByCalendar = computed(() => {
    return Object.values(OptionTarifaire)
        ?.filter((option: OptionTarifaire) => {
            const segment =
                getSourceById.value?.informations?.pdl?.compteur?.segment ?? ""
            const hasPointeFixe =
                (getPlagesHoraires.value.heures_pointe_fixe?.length ?? 0) > 0

            // Exclure HS_PTE_Fixe si aucune plage en pointe fixe
            if (option === OptionTarifaire.HS_PTE_Fixe && !hasPointeFixe) {
                return false
            }

            // HPHC et Base uniquement pour segments C5 et P4
            if ([OptionTarifaire.HPHC, OptionTarifaire.Base].includes(option)) {
                return ["C5", "P4"].includes(segment)
            }

            // HS pour C4, C5, P4, P3 ou C2/P1 sans pointe fixe
            if (option === OptionTarifaire.HS) {
                return (
                    ["C4", "C5", "P4", "P3"].includes(segment) ||
                    (["C2", "P1"].includes(segment) && !hasPointeFixe)
                )
            }

            return true
        })
        .map((option: OptionTarifaire) => ({
            label: t(`offer_type_options.${option}`),
            value: option,
        }))
})

// ================================
// LOGIQUE MÉTIER - FORM CONFIGURATION DES CONSOMMATIONS
// ================================
/**
 * Retourne la configuration des champs pour la capacité selon le mode sélectionné
 */
const getConsommationParPosteFields = computed(
    (): CourtageFormFieldConfig[] => {
        const fields: CourtageFormFieldConfig[] = []

        if (!props.actual) return fields

        const options = {
            group_suffix: t("energieparposte_elec_form.suffix"),
            minFractionDigits: 0,
            maxFractionDigits: 3,
            min: 0,
            locale: "fr-FR",
        }

        switch (props.offer) {
            case OptionTarifaire.Base:
                fields.push({
                    label: t("energieparposte_elec_form.fields.Base.title"),
                    inputId: "Base",
                    component: BaseInputNumber,
                    hint: t("energieparposte_elec_form.fields.Base.tooltip"),
                    options,
                    model: modelEnergieBase,
                })
                break
            case OptionTarifaire.HPHC:
                ;["HP", "HC"].forEach((key) => {
                    const model = key === "HP" ? modelEnergieHp : modelEnergieHc

                    fields.push({
                        label: t(
                            `energieparposte_elec_form.fields.${key}.title`
                        ),
                        inputId: key,
                        component: BaseInputNumber,
                        hint: t(
                            `energieparposte_elec_form.fields.${key}.tooltip`
                        ),
                        options,
                        model,
                    })
                })
                break

            case OptionTarifaire.HS:
            case OptionTarifaire.HS_PTE_Fixe:
                ;["HPB", "HCB", "HPH", "HCH"].forEach((key) => {
                    const dictModel = {
                        HPB: modelEnergieHpb,
                        HCB: modelEnergieHcb,
                        HPH: modelEnergieHph,
                        HCH: modelEnergieHch,
                    }

                    const model = dictModel[key as keyof typeof dictModel]

                    fields.push({
                        label: t(
                            `energieparposte_elec_form.fields.${key}.title`
                        ),
                        inputId: key,
                        component: BaseInputNumber,
                        hint: t(
                            `energieparposte_elec_form.fields.${key}.tooltip`
                        ),
                        options,
                        model,
                    })
                })
                // Dans le cas de l'option HS_PTE_Fixe, on ajoute le champ pour la Pointe
                if (props.offer === OptionTarifaire.HS_PTE_Fixe) {
                    fields.push({
                        label: t(`energieparposte_elec_form.fields.PTE.title`),
                        inputId: "PTE",
                        component: BaseInputNumber,
                        hint: t("energieparposte_elec_form.fields.PTE.tooltip"),
                        options,
                        model: modelEnergiePte,
                    })
                }
                break
        }

        return fields
    }
)

// ================================
// LOGIQUE MÉTIER - CONSOMMATION PAR PLAGE
// ================================
/**
 * Formate la consommation annuelle selon les périodes HP/HC (valeurs en MWh/an)
 * @returns Objet plage → consommation en MWh/an
 */
const getConsommationAnnuelleFormatted = computed(() => {
    if (Object.keys(snapshotConsommationByPlageFournisseur.value).length > 0) {
        // Sort entries according to PLAGE_ORDER
        const sortedEntries = Object.entries(
            snapshotConsommationByPlageFournisseur.value
        ).sort(([plageA], [plageB]) => {
            const indexA = PLAGE_ORDER.indexOf(plageA as any)
            const indexB = PLAGE_ORDER.indexOf(plageB as any)

            // Handle cases where plage might not be in PLAGE_ORDER
            if (indexA === -1) return 1
            if (indexB === -1) return -1

            return indexA - indexB
        })

        const reducedEntries = sortedEntries.reduce(
            (acc, [plage, consommation]) => {
                if (acc?.[plage]) {
                    return acc
                }
                // Conversion kWh → MWh pour l'affichage
                acc[plage] = consommation / 1000
                return acc
            },
            {} as Record<string, number>
        )

        // Renome "HPB" et "HCB" en "HPE" et "HCE"
        if (reducedEntries["HPB"] !== undefined) {
            reducedEntries["HPE"] = reducedEntries["HPB"]
            delete reducedEntries["HPB"]
        }
        if (reducedEntries["HCB"] !== undefined) {
            reducedEntries["HCE"] = reducedEntries["HCB"]
            delete reducedEntries["HCB"]
        }

        return reducedEntries
    }

    return "-"
})

// ================================
// AFFICHAGE - IMAGE FOURNISSEUR
// ================================
/**
 * Image et nom alternatif du fournisseur sélectionné
 */
const getSupplierImage = computed<{ img: string; alt: string } | null>(() => {
    if (!modelSupplier.value) return null

    const supplier = suppliers.value.find(
        (supplier: Supplier) => supplier._id_supplier === modelSupplier.value
    )

    return supplier ? { img: supplier.img, alt: supplier.name } : null
})

/**
 * Indique si le formulaire est valide
 */
const getIsValid = computed(() => {
    return meta.value.valid
})

// ================================
// WATCHERS - SYNCHRONISATION
// ================================
/**
 * Synchronise le fournisseur avec le composant parent
 */
watch(modelSupplier, (value: string | undefined) => {
    emit("update:supplier", value ?? "")
})

/**
 * Synchronise le nom de l'offre avec le composant parent
 */
watch(modelName, (value: string | undefined) => {
    emit("update:name", value ?? "")
})

/**
 * Synchronise le type d'offre avec le composant parent
 */
watch(
    modelOffer,
    (value: OptionTarifaire | "" | undefined) => {
        emit("update:offer", value ?? "")

        if (!value || !props.actual) {
            return
        }
        comparatorStore.fetchConsoParPlage({
            offer: value,
            callback: (data: Record<string, number>) => {
                modelEnergieBase.value = data["Base"] ?? 0
                modelEnergieHp.value = data["HP"] ?? 0
                modelEnergieHc.value = data["HC"] ?? 0
                modelEnergieHph.value = data["HPH"] ?? 0
                modelEnergieHch.value = data["HCH"] ?? 0
                modelEnergieHpb.value = data["HPB"] ?? 0
                modelEnergieHcb.value = data["HCB"] ?? 0
                modelEnergiePte.value = data["PTE"] ?? 0
            },
            callbackError: () => {
                consommationByPlageFournisseur.value = {}
                useEnergieparposteElecStore.value = false
            },
        })
    },
    {
        immediate: true,
    }
)

/**
 * Synchronise l'usage de la consommation par poste électrique avec le composant parent
 */
watch(
    modelUseEnergieparposteElec,
    (value: boolean | undefined) => {
        emit("update:use-energieparposte-elec", value ?? false)
    },
    { immediate: true }
)

/**
 * Synchronise les données de consommation par poste électrique avec le composant parent
 */
watch(
    [
        modelEnergieBase,
        modelEnergieHp,
        modelEnergieHc,
        modelEnergieHph,
        modelEnergieHch,
        modelEnergieHpb,
        modelEnergieHcb,
        modelEnergiePte,
    ],
    () => {
        let energieparposteElec: ComparatorEnergieParPosteElec = {}

        switch (modelOffer.value) {
            case OptionTarifaire.Base:
                energieparposteElec = {
                    Base: modelEnergieBase.value || 0,
                }
                break
            case OptionTarifaire.HPHC:
                energieparposteElec = {
                    HP: modelEnergieHp.value || 0,
                    HC: modelEnergieHc.value || 0,
                }
                break
            case OptionTarifaire.HS:
                energieparposteElec = {
                    HPH: modelEnergieHph.value || 0,
                    HCH: modelEnergieHch.value || 0,
                    HPB: modelEnergieHpb.value || 0,
                    HCB: modelEnergieHcb.value || 0,
                }
                break
            case OptionTarifaire.HS_PTE_Fixe:
                energieparposteElec = {
                    HPH: modelEnergieHph.value || 0,
                    HCH: modelEnergieHch.value || 0,
                    HPB: modelEnergieHpb.value || 0,
                    HCB: modelEnergieHcb.value || 0,
                    PTE: modelEnergiePte.value || 0,
                }
                break
            default:
                energieparposteElec = {}
        }
        emit("update:energieparposte-elec", energieparposteElec)
    },
    { immediate: true }
)

/**
 * Ajuste automatiquement le type d'offre si la source change et que le type actuel n'est plus disponible
 */
watch(
    getSourceById,
    () => {
        const isOfferTypeAvailable = getOfferTypeByCalendar.value.find(
            (option) => option.value === modelOffer.value
        )

        if (!isOfferTypeAvailable) {
            // Si une seule option disponible, la sélectionner automatiquement
            modelOffer.value = getOfferTypeByCalendar.value[0]?.value || ""
        }
    },
    { immediate: true }
)

// ================================
// API PUBLIQUE DU COMPOSANT
// ================================
defineExpose({
    isValid: getIsValid,
    validate,
    expand: () => (active.value = "0"),
    collapse: () => (active.value = null),
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL - ACCORDÉON -->
    <!-- ================================ -->
    <BaseAccordion
        :value="active"
        @tab-close="active = null"
        @tab-open="active = '0'"
        class="comparator-offer-item-info-general"
    >
        <BaseAccordionPanel value="0">
            <!-- ================================ -->
            <!-- HEADER - TITRE ET VALIDATION -->
            <!-- ================================ -->
            <BaseAccordionHeader
                class="comparator-offer-item-info-general__header flex-row-reverse! justify-end! gap-2!"
            >
                <div class="flex gap-2 items-center justify-between w-full">
                    <div class="flex gap-2 items-center">
                        <h5 class="pl-2! text-xs sm:text-base">
                            {{ t("title", { actual }) }}
                        </h5>
                        <i
                            class="pi pi-check"
                            :class="{ 'text-green-500': getIsValid }"
                        ></i>
                    </div>
                    <BaseButton
                        link
                        :label="t('edit', { edit: active === '0' })"
                        :icon="`pi pi-${active === '0' ? 'check' : 'pencil'}`"
                        icon-pos="right"
                        :pt="{
                            label: 'text-primary-700',
                            icon: 'text-primary-700',
                        }"
                    ></BaseButton>
                </div>
            </BaseAccordionHeader>

            <!-- ================================ -->
            <!-- CONTENU - FORMULAIRE -->
            <!-- ================================ -->
            <BaseAccordionContent :pt="{ content: 'rounded-b-md' }">
                <form
                    @submit.prevent.stop
                    class="comparator-offer-item-info-general__content flex flex-col gap-4"
                >
                    <!-- ================================ -->
                    <!-- IMAGE FOURNISSEUR -->
                    <!-- ================================ -->
                    <div>
                        <img
                            v-if="getSupplierImage?.img"
                            :src="getSupplierImage.img"
                            :alt="getSupplierImage.alt"
                            class="supplier-placeholder w-14 h-14 p-0.5 rounded-md border border-gray-300 text-xl flex items-center justify-center object-contain"
                        />
                        <div
                            v-else
                            class="supplier-placeholder w-14 h-14 rounded-md bg-gray-200 text-xl flex items-center justify-center"
                        >
                            {{
                                getSupplierImage?.alt?.charAt(0) &&
                                getSupplierImage?.alt !== "Autre"
                                    ? getSupplierImage?.alt?.charAt(0)
                                    : "F"
                            }}
                        </div>
                    </div>

                    <!-- ================================ -->
                    <!-- CHAMPS DU FORMULAIRE -->
                    <!-- ================================ -->
                    <div
                        class="comparator-offer-item-info-general__fields grid xl:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-4 items-start"
                    >
                        <!-- Nom fournisseur -->
                        <BaseInputWrapper
                            input-id="supplier"
                            :label="t('supplier')"
                            unfloat
                            :error="errors.supplier"
                        >
                            <BaseSelect
                                id="supplier"
                                :options="suppliers"
                                option-value="_id_supplier"
                                option-label="name"
                                v-model="modelSupplier"
                                aria-required
                                filter
                                show-clear
                                :loading="isFetchingSuppliers"
                                :invalid="!!errors.supplier"
                            >
                                <template #option="{ option }">
                                    <div class="flex items-center gap-2">
                                        <!-- Logo du fournisseur -->
                                        <div
                                            class="w-6 h-6 rounded-md flex items-center justify-center text-sm font-light text-gray-700"
                                            :class="
                                                !option?.img
                                                    ? 'bg-gray-200'
                                                    : 'bg-transparent'
                                            "
                                        >
                                            <span v-if="!option?.img">{{
                                                option.name !== "Autre"
                                                    ? option.name.charAt(0)
                                                    : "F"
                                            }}</span>
                                            <img
                                                v-else-if="option?.img"
                                                :src="option.img"
                                                :alt="option.name"
                                                class="w-7 h-7 rounded-md object-contain"
                                            />
                                        </div>
                                        <span>{{ option.name }}</span>
                                    </div>
                                </template>
                            </BaseSelect>
                        </BaseInputWrapper>

                        <!-- Nom offre -->
                        <BaseInputWrapper
                            input-id="name"
                            :label="t('offer_name')"
                            unfloat
                            :error="errors.name"
                        >
                            <BaseInputText
                                v-model="modelName"
                                aria-required
                                :invalid="!!errors.name"
                            />
                        </BaseInputWrapper>

                        <!-- Type offre -->
                        <BaseInputWrapper
                            input-id="offer"
                            :label="t('offer_type')"
                            unfloat
                            :error="errors.offer"
                        >
                            <BaseSelect
                                id="offer"
                                :options="getOfferTypeByCalendar"
                                option-value="value"
                                option-label="label"
                                option-disable="disabled"
                                v-model="modelOffer"
                                aria-required
                                :invalid="!!errors.offer"
                            />
                        </BaseInputWrapper>
                        <!-- Toggle pour surcharger les consommations par poste -->

                        <BaseInputWrapper
                            v-if="actual && modelOffer"
                            input-id="useEnergieparposteElec"
                            unfloat
                            orientation="horizontal"
                            :error="errors.useEnergieparposteElec"
                            :label="t('use_energieparposte_elec')"
                            class="md:items-end! md:pb-2!"
                        >
                            <BaseToggleSwitch
                                v-model="modelUseEnergieparposteElec"
                                :invalid="!!errors.useEnergieparposteElec"
                            />
                        </BaseInputWrapper>
                    </div>
                    <!-- Champs dynamiques pour les consommations par poste électrique -->
                    <div v-if="actual">
                        <BaseDivider />
                        <h4 class="font-semibold text-base mb-4">
                            {{ t("energieparposte_elec_form.title") }}
                        </h4>
                        <MeterPdlDeniveleViewer
                            v-if="
                                !isFetchingConsoParPlage &&
                                !modelUseEnergieparposteElec
                            "
                            :denivele="getConsommationAnnuelleFormatted"
                            unit="MWh/an"
                        />
                        <BaseSkeleton
                            v-else-if="
                                isFetchingConsoParPlage &&
                                !modelUseEnergieparposteElec
                            "
                            height="4rem"
                            width="100%"
                        />

                        <div
                            v-if="
                                !isFetchingConsoParPlage &&
                                modelUseEnergieparposteElec
                            "
                            class="comparator-offer-item-info-general__fields grid xl:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-4 items-start"
                        >
                            <BaseInputWrapper
                                v-for="field in getConsommationParPosteFields"
                                :key="field.inputId"
                                :label="field.label"
                                :input-id="field.inputId"
                                unfloat
                                :orientation="field.orientation"
                                :suffix="field.options?.group_suffix"
                                :error="
                                    errors[field.inputId as keyof typeof errors]
                                "
                            >
                                <template #label>
                                    <i
                                        class="pi pi-info-circle ml-1"
                                        v-tooltip="field.hint"
                                        style="font-size: 0.8rem"
                                    ></i>
                                </template>
                                <component
                                    :id="field.inputId"
                                    :is="field.component"
                                    v-bind="field.options"
                                    :input-id="field.inputId"
                                    v-model="field.model"
                                    :invalid="
                                        !!errors[
                                            field.inputId as keyof typeof errors
                                        ]
                                    "
                                />
                            </BaseInputWrapper>
                        </div>
                        <div
                            v-else-if="
                                isFetchingConsoParPlage &&
                                modelUseEnergieparposteElec
                            "
                            class="comparator-offer-item-info-general__fields grid xl:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-4 items-start"
                        >
                            <BaseSkeleton
                                v-for="field in getConsommationParPosteFields"
                                :key="field.inputId"
                                height="4rem"
                                width="100%"
                            />
                        </div>
                    </div>
                </form>
            </BaseAccordionContent>
        </BaseAccordionPanel>
    </BaseAccordion>
</template>
